import { useCallback, useEffect, useRef, useState } from "react";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { logQuietError } from "@/lib/quiet-error";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  ScanLine,
  Upload,
  Sparkles,
  Loader2,
  Send,
  Mic,
  WifiOff,
  RefreshCcw,
  Trash2,
  FileText,
  ListChecks,
  Layers,
  PenLine,
  Presentation,
  Languages,
  BookOpen,
  HelpCircle,
  MessageSquare,
} from "lucide-react";
import { toast } from "sonner";
import Markdown from "@/components/Markdown";
import QuickActionBar from "@/components/QuickActionBar";
import VoiceRecorder from "@/components/VoiceRecorder";
import { Skeleton } from "@/components/Skeleton";
import {
  enqueueOcr,
  listQueue,
  removeFromQueue,
  compressImage,
} from "@/lib/offline-queue";
import SmartActionShelf from "@/components/ocr/SmartActionShelf";
import ResultCanvas from "@/components/ocr/ResultCanvas";
import SimpleDialog from "@/components/ocr/SimpleDialog";

/* The Magical Action Shelf — instant smart actions on the extracted document */
const SMART_ACTIONS = [
  { key: "summarize",  label: "Summarize",   icon: FileText,     chip: "tb-chip-lavender" },
  { key: "explain",    label: "Explain",     icon: BookOpen,     chip: "tb-chip-sky" },
  { key: "quiz",       label: "Quiz",        icon: ListChecks,   chip: "tb-chip-mint" },
  { key: "worksheet",  label: "Worksheet",   icon: FileText,     chip: "tb-chip-butter" },
  { key: "flashcards", label: "Flashcards",  icon: Layers,       chip: "tb-chip-rose" },
  { key: "lesson_plan",label: "Lesson plan", icon: PenLine,      chip: "tb-chip-lavender" },
  { key: "ppt_outline",label: "Presentation",icon: Presentation, chip: "tb-chip-sky" },
  { key: "translate",  label: "Translate",   icon: Languages,    chip: "tb-chip-mint" },
  { key: "simplify",   label: "Simplify",    icon: HelpCircle,   chip: "tb-chip-butter" },
];

const LANGS_TARGET = ["English", "Hindi", "Kannada", "Tamil", "Telugu", "Marathi", "Bengali", "Arabic", "French", "Spanish"];

export default function OCRScanner() {
  const fileRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [base64, setBase64] = useState(null);
  const [mime, setMime] = useState("image/jpeg");
  const [language, setLanguage] = useState("");
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  // Tabbed canvas state — keyed by action key. "extract" is the original output.
  const [activeTab, setActiveTab] = useState("extract");
  const [actionOutputs, setActionOutputs] = useState({}); // { [actionKey]: { title, content } }
  const [actionLoading, setActionLoading] = useState(null);

  // Q&A (Ask AI)
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [qaBusy, setQaBusy] = useState(false);
  const [voiceOpen, setVoiceOpen] = useState(false);

  // Translate / simplify modal state
  const [translateOpen, setTranslateOpen] = useState(false);
  const [translateLang, setTranslateLang] = useState("Hindi");
  const [simplifyOpen, setSimplifyOpen] = useState(false);
  const [simplifyGrade, setSimplifyGrade] = useState("5");
  const [lessonOpen, setLessonOpen] = useState(false);
  const [lessonGrade, setLessonGrade] = useState("8");

  // Offline queue
  const [queueItems, setQueueItems] = useState([]);
  const [online, setOnline] = useState(typeof navigator !== "undefined" ? navigator.onLine : true);
  const [syncing, setSyncing] = useState(false);

  const refreshQueue = useCallback(async () => {
    try {
      const items = await listQueue();
      setQueueItems(items);
    } catch (e) { logQuietError("ocr.queue.refresh", e); }
  }, []);

  const syncQueue = useCallback(async () => {
    if (syncing) return;
    setSyncing(true);
    try {
      const items = await listQueue();
      let success = 0;
      for (const it of items) {
        try {
          await api.post("/ocr/extract", { image_base64: it.image_base64, mime_type: it.mime_type, language_hint: it.language_hint, title: it.title });
          await removeFromQueue(it.id); success += 1;
        } catch (e) { logQuietError("ocr.queue.sync.item", e); /* leave in queue */ }
      }
      if (success) toast.success(`Synced ${success} document${success > 1 ? "s" : ""}`);
      await refreshQueue();
    } finally { setSyncing(false); }
  }, [syncing, refreshQueue]);

  useEffect(() => {
    refreshQueue();
    const goOnline = () => { setOnline(true); syncQueue(); };
    const goOffline = () => setOnline(false);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, [refreshQueue, syncQueue]);

  const handleFile = async (file) => {
    if (!file) return;
    try {
      const compressed = await compressImage(file, 1600, 0.78);
      setPreview(compressed.dataUrl);
      setMime(compressed.mime);
      const idx = compressed.dataUrl.indexOf(",");
      setBase64(idx >= 0 ? compressed.dataUrl.slice(idx + 1) : compressed.dataUrl);
      if (!title) setTitle(file.name.replace(/\.[^.]+$/, ""));
    } catch {
      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = reader.result;
        setPreview(dataUrl);
        const m = file.type || "image/jpeg";
        setMime(m);
        const idx = String(dataUrl).indexOf(",");
        setBase64(idx >= 0 ? String(dataUrl).slice(idx + 1) : String(dataUrl));
        if (!title) setTitle(file.name.replace(/\.[^.]+$/, ""));
      };
      reader.readAsDataURL(file);
    }
  };

  const resetCanvas = () => {
    setActionOutputs({});
    setActiveTab("extract");
    setAnswer("");
    setQuestion("");
  };

  const extract = async () => {
    if (!base64) { toast.error("Please upload an image first"); return; }
    if (!navigator.onLine) {
      try {
        await enqueueOcr({ image_base64: base64, mime_type: mime, language_hint: language || null, title: title || null });
        await refreshQueue();
        toast.success("Saved offline — will auto-sync when online.");
        setPreview(null); setBase64(null); setTitle("");
      } catch { toast.error("Could not save to offline queue"); }
      return;
    }
    setBusy(true);
    setResult(null);
    resetCanvas();
    try {
      const { data } = await api.post("/ocr/extract", {
        image_base64: base64, mime_type: mime, language_hint: language || null, title: title || null,
      });
      setResult(data);
      setActiveTab("extract");
      toast.success("Extracted! Tap any smart action to continue.");
    } catch { toast.error("OCR failed"); }
    finally { setBusy(false); }
  };

  const ask = async () => {
    if (!result?.document_id || !question.trim()) return;
    setQaBusy(true);
    setAnswer("");
    try {
      const { data } = await api.post("/ocr/explain", { document_id: result.document_id, question });
      setAnswer(data.answer);
    } catch { toast.error("Failed to answer"); }
    finally { setQaBusy(false); }
  };

  const runSmartAction = async (actionKey, extras = {}) => {
    if (!result?.document_id) return;
    setActionLoading(actionKey);
    setActiveTab(actionKey);
    try {
      const payload = { document_id: result.document_id, action: actionKey, ...extras };
      const { data } = await api.post("/ocr/action", payload);
      setActionOutputs((prev) => ({ ...prev, [actionKey]: { title: data.title, content: data.content } }));
    } catch {
      toast.error("Generation failed. Try again.");
      setActiveTab("extract");
    } finally {
      setActionLoading(null);
    }
  };

  const handleSmartActionClick = (actionKey) => {
    if (!result?.document_id) { toast.error("Extract a document first"); return; }
    if (actionOutputs[actionKey]) { setActiveTab(actionKey); return; }
    if (actionKey === "translate") { setTranslateOpen(true); return; }
    if (actionKey === "simplify") { setSimplifyOpen(true); return; }
    if (actionKey === "lesson_plan") { setLessonOpen(true); return; }
    runSmartAction(actionKey);
  };

  const copyOutput = async () => {
    const out = activeTab === "extract"
      ? result?.extracted_text
      : actionOutputs[activeTab]?.content;
    if (!out) return;
    try { await navigator.clipboard.writeText(out); toast.success("Copied"); }
    catch { toast.error("Copy failed"); }
  };

  return (
    <Layout title="Document OCR & Intelligence">
      <QuickActionBar exclude={["scan"]} />
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        {/* ====== Left rail: Upload + queue ====== */}
        <aside className="lg:col-span-4 space-y-4" data-testid="ocr-input-panel">
          <div className="tb-card p-5">
            <Label>Upload image or scan</Label>
            <div
              onClick={() => fileRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files?.[0]; if (f) handleFile(f); }}
              className="mt-2 cursor-pointer rounded-2xl border-2 border-dashed border-border hover:border-primary/60 transition p-6 text-center bg-muted/20"
              data-testid="ocr-dropzone"
            >
              {preview ? (
                <img src={preview} alt="preview" className="max-h-64 mx-auto rounded-xl object-contain" />
              ) : (
                <>
                  <span className="mx-auto h-12 w-12 grid place-items-center rounded-2xl bg-primary/10 text-primary mb-3">
                    <Upload className="h-5 w-5" />
                  </span>
                  <p className="text-sm font-medium">Click or drop to upload</p>
                  <p className="text-[11px] text-muted-foreground mt-1">JPG, PNG, WEBP · auto-compressed</p>
                </>
              )}
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                hidden
                onChange={(e) => handleFile(e.target.files?.[0])}
                data-testid="ocr-file-input"
              />
            </div>
          </div>

          <div className="tb-card p-5 space-y-3">
            <div>
              <Label>Title (optional)</Label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Notebook page 1" className="mt-1.5 h-11 rounded-xl" data-testid="ocr-title-input" />
            </div>
            <div>
              <Label>Language hint (optional)</Label>
              <Input value={language} onChange={(e) => setLanguage(e.target.value)} placeholder="e.g. Hindi, Kannada, English" className="mt-1.5 h-11 rounded-xl" data-testid="ocr-lang-input" />
            </div>
            <Button onClick={extract} disabled={busy || !base64} className="w-full h-11 rounded-full" data-testid="ocr-extract-button">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" />
                : !online ? (<><WifiOff className="h-4 w-4 mr-1.5" /> Save offline</>)
                : (<><ScanLine className="h-4 w-4 mr-1.5" /> Extract & Summarize</>)}
            </Button>
          </div>

          {queueItems.length > 0 && (
            <div className="tb-card p-5" data-testid="offline-queue-card">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-display text-base font-medium">Offline queue</p>
                  <p className="text-[11px] text-muted-foreground mt-0.5">{queueItems.length} waiting</p>
                </div>
                <Button size="sm" variant="outline" className="rounded-full" onClick={syncQueue} disabled={!online || syncing} data-testid="sync-queue-button">
                  {syncing ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <RefreshCcw className="h-3.5 w-3.5 mr-1.5" />} Sync
                </Button>
              </div>
              <div className="mt-3 space-y-2">
                {queueItems.map((q) => (
                  <div key={q.id} className="flex items-center gap-2 rounded-xl border border-border bg-card p-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm truncate">{q.title || "Untitled"}</p>
                      <p className="text-[11px] text-muted-foreground">Queued · {new Date(q.queued_at).toLocaleTimeString()}</p>
                    </div>
                    <Button size="icon" variant="ghost" className="text-destructive" onClick={async () => { await removeFromQueue(q.id); refreshQueue(); }}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </aside>

        {/* ====== Right: Smart Action Shelf + Result Canvas ====== */}
        <section className="lg:col-span-8 space-y-4">
          {result && (
            <SmartActionShelf
              actions={SMART_ACTIONS}
              activeTab={activeTab}
              actionLoading={actionLoading}
              actionOutputs={actionOutputs}
              onClick={handleSmartActionClick}
            />
          )}

          {/* Result canvas */}
          <div className={`tb-card p-6 min-h-[320px] relative ${actionLoading ? "tb-tracing-border" : ""}`} data-testid="ocr-result">
            {!result && !busy && (
              <div className="grid place-items-center text-center text-muted-foreground py-16">
                <div>
                  <span className="mx-auto h-14 w-14 grid place-items-center rounded-full border border-dashed border-border mb-3">
                    <ScanLine className="h-6 w-6 opacity-60" />
                  </span>
                  <p className="font-display font-medium">Upload a document to begin</p>
                  <p className="mt-1 text-xs text-muted-foreground max-w-[34ch] mx-auto">
                    Handwritten notes, textbook pages, whiteboards, screenshots — TeeBee handles them all.
                  </p>
                </div>
              </div>
            )}
            {busy && (
              <div className="grid place-items-center text-center py-16">
                <div className="space-y-4">
                  <div className="mx-auto h-16 w-16 rounded-full tb-ai-orb grid place-items-center tb-float">
                    <Sparkles className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <p className="font-display font-medium">Reading your document</p>
                    <p className="mt-1 text-xs text-muted-foreground">TeeBee Vision is parsing layout, math and language…</p>
                  </div>
                  <div className="max-w-xs mx-auto space-y-2">
                    <Skeleton className="h-3 w-full" />
                    <Skeleton className="h-3 w-5/6" />
                    <Skeleton className="h-3 w-4/6" />
                  </div>
                </div>
              </div>
            )}
            {result && (
              <ResultCanvas
                result={result}
                title={title}
                smartActions={SMART_ACTIONS}
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                actionOutputs={actionOutputs}
                actionLoading={actionLoading}
                onCopy={copyOutput}
              />
            )}
          </div>

          {/* Ask the document */}
          {result && (
            <div className="tb-card p-5 sm:p-6" data-testid="ocr-qa">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] font-semibold text-muted-foreground">
                <MessageSquare className="h-3 w-3" /> Ask the document
              </div>
              <div className="mt-3 flex gap-2">
                <Input
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && ask()}
                  placeholder="Summarize in 3 bullets · What's the formula? · Translate to French…"
                  className="h-11 rounded-full"
                  data-testid="ocr-qa-input"
                />
                <Button variant="outline" className="h-11 w-11 p-0 rounded-full" onClick={() => setVoiceOpen(true)} data-testid="ocr-qa-voice" aria-label="Voice typing">
                  <Mic className="h-4 w-4" />
                </Button>
                <Button onClick={ask} disabled={qaBusy || !question.trim()} className="h-11 rounded-full" data-testid="ocr-qa-send">
                  {qaBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </Button>
              </div>
              {answer && <Markdown className="mt-4">{answer}</Markdown>}
            </div>
          )}
        </section>
      </div>

      <VoiceRecorder open={voiceOpen} onClose={() => setVoiceOpen(false)} onResult={(text) => setQuestion((q) => (q ? `${q} ${text}` : text))} />

      {/* Translate dialog */}
      {translateOpen && (
        <SimpleDialog title="Translate document" onClose={() => setTranslateOpen(false)}>
          <Label>Target language</Label>
          <select value={translateLang} onChange={(e) => setTranslateLang(e.target.value)} className="mt-1.5 h-11 w-full rounded-xl border border-border bg-background px-3 text-sm">
            {LANGS_TARGET.map((l) => <option key={l}>{l}</option>)}
          </select>
          <Button
            className="mt-4 w-full rounded-full h-11"
            onClick={() => { setTranslateOpen(false); runSmartAction("translate", { target_language: translateLang }); }}
            data-testid="translate-confirm"
          >
            <Languages className="h-4 w-4 mr-1.5" /> Translate to {translateLang}
          </Button>
        </SimpleDialog>
      )}

      {/* Simplify dialog */}
      {simplifyOpen && (
        <SimpleDialog title="Simplify for grade" onClose={() => setSimplifyOpen(false)}>
          <Label>Target grade</Label>
          <Input className="mt-1.5 h-11 rounded-xl" value={simplifyGrade} onChange={(e) => setSimplifyGrade(e.target.value)} placeholder="e.g. 5" />
          <Button
            className="mt-4 w-full rounded-full h-11"
            onClick={() => { setSimplifyOpen(false); runSmartAction("simplify", { target_grade: simplifyGrade }); }}
            data-testid="simplify-confirm"
          >
            <HelpCircle className="h-4 w-4 mr-1.5" /> Simplify for grade {simplifyGrade}
          </Button>
        </SimpleDialog>
      )}

      {/* Lesson plan dialog */}
      {lessonOpen && (
        <SimpleDialog title="Generate lesson plan" onClose={() => setLessonOpen(false)}>
          <Label>Target grade</Label>
          <Input className="mt-1.5 h-11 rounded-xl" value={lessonGrade} onChange={(e) => setLessonGrade(e.target.value)} placeholder="e.g. 8" />
          <Button
            className="mt-4 w-full rounded-full h-11"
            onClick={() => { setLessonOpen(false); runSmartAction("lesson_plan", { target_grade: lessonGrade }); }}
            data-testid="lesson-confirm"
          >
            <PenLine className="h-4 w-4 mr-1.5" /> Build 45-min lesson plan
          </Button>
        </SimpleDialog>
      )}
    </Layout>
  );
}
